import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';

import { createAccount } from '../api/endpoints';
import { useAdminMutation } from '../api/useAdminMutation';
import type { AccountProfile } from '../api/types';
import { ErrorNotice } from './ErrorNotice';
import { generatePassword } from './generatePassword';
import { PasswordField } from './PasswordField';

const ASSIGNABLE_ROLES: AccountProfile['role'][] = ['PARENT', 'SUPPORT', 'CONTENT_EDITOR', 'SUPERUSER'];

/**
 * Create an account with a role and a password.
 *
 * Until this existed the only way to make one with back-office access was a shell on the server running
 * `create-superuser`, with the password typed on a command line — where it lands in the shell history of
 * whoever ran it (R0142).
 */
export const CreateAccountDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<string>('PARENT');
  const [password, setPassword] = useState(generatePassword());

  // `accounts` and `audit`: creating an account writes an audit entry, so that list is stale too.
  const create = useAdminMutation(
    () => createAccount(email, role, password, displayName.trim() === '' ? undefined : displayName.trim()),
    ['accounts'],
  );

  const reset = () => {
    setEmail('');
    setDisplayName('');
    setRole('PARENT');
    // A fresh one each time the dialog opens. Reusing the previous value would put one password on two
    // accounts, silently, whenever somebody creates two in a row.
    setPassword(generatePassword());
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Nový účet</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Účet je hneď aktívny a e-mail sa považuje za overený — adresu zadáva superužívateľ, nie jej držiteľ,
          takže na overovací odkaz by sa čakalo márne.
        </DialogContentText>

        <ErrorNotice error={create.error} />

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="E-mail"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <TextField
            fullWidth
            label="Meno"
            helperText="Nepovinné."
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Rola"
            helperText="Back-office rola sprístupní údaje o všetkých deťoch na platforme."
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {ASSIGNABLE_ROLES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <PasswordField value={password} onChange={setPassword} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Zrušiť</Button>
        <Button
          variant="contained"
          disabled={email.trim().length === 0 || password.trim().length === 0 || create.isPending}
          onClick={() => create.mutate(undefined, { onSuccess: close })}
        >
          Vytvoriť
        </Button>
      </DialogActions>
    </Dialog>
  );
};
